import React, { useState } from 'react';
import { Eye, Edit3, Sigma, Bold, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import TheoryRenderer from '../course/TheoryRenderer';

// RichTextField
// ─────────────
// Универсальная замена обычному input/textarea для полей где автор хочет
// форматирование (жирный, заголовки, **списки**) или KaTeX-формулы
// ($x^2$, $$\int...$$). Используется в QuestionEditor для текста вопроса,
// вариантов ответа и пояснения, плюс везде где встречаются «короткие
// тексты с возможной математикой».
//
// Дизайн (тот же что у TheoryEditor):
//   • markdown в textarea — paste-safe by design
//   • toggle между Edit / Preview через TheoryRenderer
//   • paste принудительно сводится к plain-text → копия из Word не ломает разметку
//
// Стилевое отличие от TheoryEditor — компактный (для inline-полей в форме).
const RichTextField = ({
  value,
  onChange,
  placeholder,
  rows = 2,
  maxLength,
  multiline = true,
  // Подсказка под полем — обычно про поддержку формул («$x^2$»).
  // Если null — не показываем (чтобы не шуметь под каждым input).
  hint = null,
}) => {
  const [mode, setMode] = useState('edit'); // 'edit' | 'preview'

  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text/plain');
    if (text == null) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const next = (value || '').slice(0, start) + text + (value || '').slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      {/* Mini toolbar — компактнее чем TheoryEditor. Только индикатор «можно
          формулы / жирный» + переключатель Edit/Preview. */}
      <div className="flex items-center justify-between gap-2 px-2 py-1 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
        <div className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
          <Bold className="w-3 h-3" /> **жирный**
          <span className="mx-1">·</span>
          <Sigma className="w-3 h-3" /> $x^2$
          <Link
            to="/help/formatting"
            className="ml-2 inline-flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400 hover:underline"
            title="Справка по форматированию"
          >
            <HelpCircle className="w-3 h-3" />
          </Link>
        </div>
        <div className="flex rounded-md bg-white dark:bg-gray-900 p-0.5 border border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setMode('edit')}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              mode === 'edit'
                ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300'
                : 'text-gray-500'
            }`}
            aria-label="Редактирование"
          >
            <Edit3 className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            disabled={!value?.trim()}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
              mode === 'preview'
                ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300'
                : 'text-gray-500'
            } disabled:opacity-40`}
            aria-label="Предпросмотр"
          >
            <Eye className="w-3 h-3" />
          </button>
        </div>
      </div>

      {mode === 'edit' ? (
        multiline ? (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            rows={rows}
            maxLength={maxLength}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-transparent outline-none resize-y placeholder:text-gray-400 break-words"
          />
        ) : (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            maxLength={maxLength}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-transparent outline-none placeholder:text-gray-400"
          />
        )
      ) : (
        <div className="px-3 py-2 min-h-[40px] bg-gray-50 dark:bg-gray-800/30">
          {value?.trim() ? (
            <TheoryRenderer content={value} />
          ) : (
            <p className="text-xs text-gray-400 italic">Превью появится когда будет текст</p>
          )}
        </div>
      )}

      {hint && (
        <div className="px-3 pb-2 text-[10px] text-gray-500 dark:text-gray-400">
          {hint}
        </div>
      )}
    </div>
  );
};

export default RichTextField;
